import {
	getFragmentsFromSelection,
	getLocalIdsFromSelection,
} from '@atlaskit/editor-common/selection';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { ROVO_AGENT_UUID } from '@atlassian/conversation-assistant-utils/default-agent';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import { InlineInputBoxSendMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-send-message-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { cancelRemixThinking, registerAbortRequest } from '../ui/utils/remix-state';

export const removeBackground = (
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined,
	state: EditorState,
): boolean => {
	if (!api || !UNSAFE_expValNoExposure('cc_image_remove_background', 'isEnabled', false)) {
		return false;
	}

	const contentId =
		api.contextIdentifier?.sharedState.currentState()?.contextIdentifierProvider?.objectId;
	const selectionFragment = getFragmentsFromSelection(state.selection);
	const localIds = getLocalIdsFromSelection(state.selection);
	const mediaNode = selectionFragment
		?.flatMap((node) => (node.type === 'mediaSingle' ? (node.content ?? []) : [node]))
		.find((node) => node?.type === 'media');
	const mediaAttributes = mediaNode?.attrs;
	const mediaFileId = mediaAttributes && 'id' in mediaAttributes ? mediaAttributes.id : undefined;
	const mediaCollection =
		mediaAttributes && 'collection' in mediaAttributes ? mediaAttributes.collection : undefined;
	const targetId = localIds?.[0];

	if (!contentId || !targetId || typeof mediaFileId !== 'string' || !mediaFileId) {
		return false;
	}

	const creationContextParams: CreationContextParams = {
		dynamicUiType: 'image',
		experience: 'remix_edit',
		source: 'remix',
		mediaFileId,
		dynamicUiSource: {
			contentType: 'page',
			startLocalId: targetId,
			endLocalId: localIds?.[localIds.length - 1],
			startIndex: state.selection.$from.parentOffset,
			endIndex: state.selection.$to.parentOffset,
			fragmentAdf: selectionFragment,
		},
		additionalContext: {
			imageEditAction: 'removeBackground',
			...(typeof mediaCollection === 'string' ? { mediaCollection } : {}),
		},
	};

	const admission = api.aiExperience?.actions.beginInlineRovoEdit?.(targetId);
	if (admission?.status === 'rejected') {
		return false;
	}

	const attemptId = admission?.status === 'admitted' ? admission.attemptId : undefined;
	const bridge = getBrowserBridgeAPI();
	const senderId = getConfluenceEditorClientId(contentId);

	api.aiExperience?.actions.stageInlineRovoStreaming?.(targetId);
	api.aiExperience?.actions.commitInlineRovoStreaming?.();
	api.aiExperience?.actions.toggleAiEntryPoints?.(true);
	api.core.actions.execute(
		api.malleableUi?.commands.startRemixThinking({
			isUpdate: true,
			remixAttemptId: attemptId,
		}),
	);

	registerAbortRequest(api, () => {
		bridge.send(InlineInputBoxAbortMessageCommand, { senderId, receiverId: ROVO_CHAT_CLIENT_ID });
	});

	const invocation = bridge.send(InlineInputBoxSendMessageCommand, {
		senderId,
		receiverId: ROVO_CHAT_CLIENT_ID,
		prompt: 'Remove the background from this image.',
		productKey: 'confluence',
		agentId: ROVO_AGENT_UUID,
		source: 'CONFLUENCE_INLINE_EDITOR',
		contentId,
		creationContextParams,
		inlineRovoEditAttemptId: attemptId,
		presentation: 'editor',
		deepResearchEnabled: false,
		webSearchEnabled: false,
		openChat: false,
		openChatOnError: false,
		shouldStartNewConversation: true,
	});

	void invocation.result().catch(() => {
		if (
			!attemptId ||
			api.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-error')
		) {
			cancelRemixThinking({ api, reopenModal: false });
		}
	});

	return true;
};
